import { useCallback, useEffect } from 'react'

import { createPersistedPreference } from '@/lib/hooks/createPersistedPreference'

const collapsedPreference = createPersistedPreference<boolean>({
  key: 'titan.sidebar-collapsed',
  fallback: false,
  parse: (raw) => (raw === 'true' ? true : raw === 'false' ? false : undefined),
})

const isApplePlatform = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent)

/** Human-readable form of the sidebar toggle shortcut for the current platform. */
export const SIDEBAR_TOGGLE_SHORTCUT_LABEL = isApplePlatform()
  ? '⌘ B'
  : 'Ctrl B'

/** Value for `aria-keyshortcuts` on controls bound to the sidebar shortcut. */
export const SIDEBAR_TOGGLE_ARIA_SHORTCUT = isApplePlatform()
  ? 'Meta+B'
  : 'Control+B'

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

/** Whether the sidebar rail is collapsed to icons only. */
export const useSidebarCollapsed = (): {
  isCollapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  toggleCollapsed: () => void
} => {
  const [isCollapsed, setCollapsed] = collapsedPreference.usePreference()

  const toggleCollapsed = useCallback(
    () => setCollapsed(!isCollapsed),
    [isCollapsed, setCollapsed]
  )

  return { isCollapsed, setCollapsed, toggleCollapsed }
}

/** Binds the platform sidebar shortcut (`⌘B` / `Ctrl+B`) to a toggle. */
export const useSidebarToggleShortcut = (toggle: () => void): void => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'b' && event.key !== 'B') return
      if (event.altKey || event.shiftKey) return
      if (!(isApplePlatform() ? event.metaKey : event.ctrlKey)) return
      if (isTypingTarget(event.target)) return
      event.preventDefault()
      toggle()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggle])
}
